// M2 economy & politics: one daily entry point for tick.js plus the small hooks other sim files call.
// Everything returns early before 1901, so the Pioneer era (M1) is bit-for-bit unchanged.
import { dayToDate } from '../../core/time.js';
import { fieldDef, yf } from '../defs.js';
import { flagOn, tickProrationMonthly } from './proration.js';
import { tickTaxesQuarterly, chargeSeverance } from './taxes.js';
import { tickPolitics, chargePartnerShare, applyRegionEvent, resolveKidnappingImpl, resolveLaborStrikeImpl, resolvePartnerOfferImpl, M2_FROM } from './politics.js';
import { tickLicensing, tickLicensingMonthly, resolveInfringementImpl, resolvePatentSuitImpl } from './licensing.js';
import { tickCores } from './geophysics.js';
import { routePricedTransport } from '../m3/routes.js'; // M3 econ: tanker charters priced by route freight

export { M2_FROM };

// tick.js: after tickContracts, before tickFinance (so the bank sees today's taxes and fines).
export function tickM2(state) {
  if (yf(state) < M2_FROM) return;
  const { dom } = dayToDate(state.day);
  if (dom === 1) {
    tickTaxesQuarterly(state);
    tickProrationMonthly(state);
    tickLicensingMonthly(state);
  }
  tickPolitics(state);
  tickLicensing(state);
  tickCores(state);
}

// events.js tickNews: M2 news effects the data layer cannot apply (regionEvent).
export function applyM2NewsEffect(state, item) {
  if (item?.effect?.type === 'regionEvent') applyRegionEvent(state, item);
}

// production.shipAndSell: after a sale of `gross` era $ from a field.
export function onSale(state, fieldId, gross) {
  if (!(gross > 0) || yf(state) < M2_FROM) return;
  chargeSeverance(state, fieldId, gross);
  chargePartnerShare(state, fieldId, gross);
}

// production.shipAndSell: fraction of shipped barrels that arrive (U-boats sink coastal tankers, 1942-43).
export const TANKER_WAR_LOSS = 0.02; // ~0.2%/day `warRisk` over a ~10-day coastal voyage
export function deliveredMult(state, fieldId, tdef) {
  if (!tdef || yf(state) < M2_FROM) return 1;
  const risk = tdef.stats?.warRisk ?? 0;
  return risk > 0 && flagOn(state, 'uboatTankerLosses') ? 1 - TANKER_WAR_LOSS : 1;
}

// Pipeline tariffs did not follow the CPI: scale, steel line pipe and the common-carrier rules pushed gathering and
// trunk-line charges from ~20¢ (1901) to ~5-15¢ a barrel in the 1930s. Multiplier on the CPI-scaled M1 tariff.
export const PIPELINE_TARIFF_KEYS = [[1901, 1], [1915, 0.7], [1930, 0.45], [1945, 0.4]];
const PIPE_IDS = new Set(['gathering_pipe', 'trunk_pipeline', 'common_carrier_pipeline']);
export function pipelineTariffMult(y) {
  const k = PIPELINE_TARIFF_KEYS;
  if (y <= k[0][0]) return k[0][1];
  for (let i = 1; i < k.length; i++) if (y <= k[i][0]) return k[i - 1][1] + (k[i][1] - k[i - 1][1]) * (y - k[i - 1][0]) / (k[i][0] - k[i - 1][0]);
  return k[k.length - 1][1];
}

// Ocean freight to world markets (region.transportToMarket, quoted in 1859 $) fell far faster than the CPI rose once
// bulk steam tankers took over: ~$0.8/bbl Tampico-Gulf in 1859 $ becomes ~40¢ in 1925, not $1.60.
export const FREIGHT_KEYS = [[1901, 1], [1912, 0.5], [1925, 0.25], [1945, 0.22]];
export function regionFreightMult(state) {
  const y = yf(state);
  if (y < M2_FROM) return 1;
  const k = FREIGHT_KEYS;
  for (let i = 1; i < k.length; i++) if (y <= k[i][0]) return k[i - 1][1] + (k[i][1] - k[i - 1][1]) * (y - k[i - 1][0]) / (k[i][0] - k[i - 1][0]);
  return k[k.length - 1][1];
}

// production.transportCostPerBbl: pipeline tariff curve, war-risk tanker rates (×3, 1942-43) and the Big Inch
// (Texas pipelines ×0.7, from Aug 1943).
export function transportMult(state, fieldId, tdef) {
  if (!tdef || yf(state) < M2_FROM) return 1;
  if (routePricedTransport(state, fieldId, tdef)) return 0; // M3 econ: freight is in regionShipping (route × ship class)
  let m = PIPE_IDS.has(tdef.id) ? pipelineTariffMult(yf(state)) : 1;
  if ((tdef.stats?.warRisk ?? 0) > 0 && flagOn(state, 'uboatTankerLosses')) m *= 3;
  if (/pipeline/.test(tdef.id) && fieldDef(fieldId)?.regionId === 'texas' && flagOn(state, 'bigInch')) m *= 0.7;
  return m;
}

// core/actions.resolveDecision: the M2 decision kinds.
export const M2_DECISION_KINDS = new Set(['kidnapping', 'laborStrike', 'partnerOffer', 'infringement', 'patentSuit']);
export function resolveM2Decision(state, d, choice) {
  switch (d.kind) {
    case 'kidnapping': return resolveKidnappingImpl(state, d.kidnapId ?? d.id, choice);
    case 'laborStrike': return resolveLaborStrikeImpl(state, d.id, choice);
    case 'partnerOffer': return resolvePartnerOfferImpl(state, d.id, choice);
    case 'infringement': return resolveInfringementImpl(state, d.infringementId ?? d.id, choice);
    case 'patentSuit': return resolvePatentSuitImpl(state, d.suitId ?? d.id, choice);
    default: return { ok: false, msg: 'Unknown decision.' };
  }
}

